import { rankOverlayWindowZIndexes } from '@supply-mover-2-0/core';
import { ComponentType } from 'react';

export type OverlayPosition =
	| 'center'
	| 'top'
	| 'bottom'
	| 'left'
	| 'right'
	| 'floating'
	| 'custom'
	| 'auto';
export type OverlayType = 'modal' | 'floating' | 'toast' | 'popup';

export interface OverlayConfig {
	id: string;
	component: ComponentType<unknown>;
	props?: Record<string, unknown>;
	type: OverlayType;
	position: OverlayPosition;
	zIndex?: number;
	backdrop?: boolean;
	backdropOpacity?: number;
	draggable?: boolean;
	resizable?: boolean;
	minimizable?: boolean;
	dismissible?: boolean;
	persistent?: boolean; // Survives navigation changes
	customStyle?: Record<string, unknown>;
	animation?: 'slide' | 'fade' | 'scale' | 'none';
	onShow?: () => void;
	onHide?: () => void;
	onBeforeHide?: () => void; // Called before hiding, useful for cleanup
	onBackdropPress?: () => void;
	onMinimize?: () => void;
	// New responsive properties
	responsive?: boolean;
	toolbarContent?: ComponentType<unknown>;
	toolbarProps?: Record<string, unknown>;
	showDragHandle?: boolean;
	showMinimize?: boolean;
	showClose?: boolean;
	showModalHeader?: boolean; // Whether to show the modal header with buttons (defaults to true)
}

export interface OverlayState extends OverlayConfig {
	isVisible: boolean;
	isMinimized?: boolean;
	isCollapsed?: boolean; // For mobile bottom sheet collapsed state
	overlayPosition?: { x: number; y: number }; // Renamed to avoid conflict with position from OverlayConfig
	size?: { width: number; height: number };
	createdAt: Date;
	updatedAt: Date;
}

type OverlayChangeListener = (overlays: Array<OverlayState>) => void;

export class OverlayService {
	private static instance: OverlayService;
	private overlays = new Map<string, OverlayState>();
	private listeners = new Set<OverlayChangeListener>();
	/**
	 * Ids of the overlays whose z-index this service assigns, bottom to top.
	 * Overlays shown with an explicit `zIndex` keep it and are not listed.
	 */
	private _stackOrder: Array<string> = [];

	private constructor() {}

	public static getInstance(): OverlayService {
		if (!OverlayService.instance) {
			OverlayService.instance = new OverlayService();
		}

		return OverlayService.instance;
	}

	/**
	 * Show an overlay with the given configuration
	 */
	public show(config: OverlayConfig): void {
		const existingOverlay = this.overlays.get(config.id);

		const overlayState: OverlayState = {
			...config,
			isVisible: true,
			isMinimized: false,
			zIndex: config.zIndex,
			createdAt: existingOverlay?.createdAt ?? new Date(),
			updatedAt: new Date(),
		};

		this.overlays.set(config.id, overlayState);
		// A newly shown overlay goes on top of the others.
		if (config.zIndex == null) {
			this._moveToTop(config.id);
		} else {
			this._stackOrder = this._stackOrder.filter((id) => id !== config.id);
		}
		this.notifyListeners();

		// Call onShow callback
		config.onShow?.();
	}

	/**
	 * Hide an overlay by ID
	 */
	public hide(id: string): void {
		const overlay = this.overlays.get(id);

		if (overlay) {
			// Call onBeforeHide for cleanup
			overlay.onBeforeHide?.();

			overlay.isVisible = false;
			overlay.updatedAt = new Date();
			this._restack();
			this.notifyListeners();

			// Call onHide callback
			overlay.onHide?.();

			// Remove non-persistent overlays after hide
			if (!overlay.persistent) {
				setTimeout(() => this.remove(id), 300); // Allow animation time
			}
		}
	}

	/**
	 * Remove an overlay completely
	 */
	public remove(id: string): void {
		if (this.overlays.has(id)) {
			this.overlays.delete(id);
			this._stackOrder = this._stackOrder.filter(
				(stackedId) => stackedId !== id,
			);
			this._restack();
			this.notifyListeners();
		}
	}

	/**
	 * Update overlay properties
	 */
	public update(id: string, updates: Partial<OverlayConfig>): void {
		const overlay = this.overlays.get(id);

		if (overlay) {
			Object.assign(overlay, updates, { updatedAt: new Date() });
			this.notifyListeners();
		}
	}

	/**
	 * Toggle minimize state for an overlay
	 */
	public toggleMinimize(id: string): void {
		const overlay = this.overlays.get(id);

		if (overlay && overlay.minimizable) {
			overlay.isMinimized = !overlay.isMinimized;
			overlay.updatedAt = new Date();
			this.notifyListeners();
		}
	}

	/**
	 * Set overlay position (for draggable overlays)
	 */
	public setPosition(id: string, position: { x: number; y: number }): void {
		const overlay = this.overlays.get(id);

		if (overlay && overlay.draggable) {
			overlay.overlayPosition = position;
			overlay.updatedAt = new Date();
			this.notifyListeners();
		}
	}

	/**
	 * Set overlay size (for resizable overlays)
	 */
	public setSize(id: string, size: { width: number; height: number }): void {
		const overlay = this.overlays.get(id);

		if (overlay && overlay.resizable) {
			overlay.size = size;
			overlay.updatedAt = new Date();
			this.notifyListeners();
		}
	}

	/**
	 * Get all visible overlays sorted by z-index
	 */
	public getVisibleOverlays(): Array<OverlayState> {
		return Array.from(this.overlays.values())
			.filter((overlay) => overlay.isVisible)
			.sort((a, b) => (a.zIndex ?? 0) - (b.zIndex ?? 0));
	}

	/**
	 * Get overlay by ID
	 */
	public getOverlay(id: string): OverlayState | undefined {
		return this.overlays.get(id);
	}

	/**
	 * Check if any overlay is visible
	 */
	public hasVisibleOverlays(): boolean {
		return Array.from(this.overlays.values()).some(
			(overlay) => overlay.isVisible,
		);
	}

	/**
	 * Hide all overlays (useful for navigation changes)
	 */
	public hideAll(exceptPersistent = true): void {
		Array.from(this.overlays.values()).forEach((overlay) => {
			if (overlay.isVisible && !(exceptPersistent && overlay.persistent)) {
				this.hide(overlay.id);
			}
		});
	}

	/**
	 * Clear all overlays
	 */
	public clear(): void {
		this.overlays.clear();
		this._stackOrder = [];
		this.notifyListeners();
	}

	/**
	 * Subscribe to overlay state changes
	 */
	public subscribe(listener: OverlayChangeListener): () => void {
		this.listeners.add(listener);

		// Return unsubscribe function
		return () => {
			this.listeners.delete(listener);
		};
	}

	/**
	 * Bring overlay to front
	 */
	public bringToFront(id: string): void {
		const overlay = this.overlays.get(id);

		if (overlay) {
			this._moveToTop(id);
			overlay.updatedAt = new Date();
			this.notifyListeners();
		}
	}

	/**
	 * Handle backdrop press for dismissible overlays
	 */
	public handleBackdropPress(id: string): void {
		const overlay = this.overlays.get(id);

		if (overlay) {
			// Call custom backdrop handler first
			if (overlay.onBackdropPress) {
				overlay.onBackdropPress();
			} else if (overlay.dismissible) {
				// Default behavior: hide overlay
				this.hide(id);
			}
		}
	}

	/**
	 * Puts an overlay at the top of the stacking order and re-ranks the rest.
	 */
	private _moveToTop(id: string): void {
		this._stackOrder = [
			...this._stackOrder.filter((stackedId) => stackedId !== id),
			id,
		];
		this._restack();
	}

	/**
	 * Gives every visible overlay a z-index from its rank among the visible
	 * overlays (see `rankOverlayWindowZIndexes`). Re-ranking on every change,
	 * rather than handing out the next value of a counter, keeps the windows
	 * between 2001 and the number open, below the popover tier, and never lets
	 * the order wrap around.
	 */
	private _restack(): void {
		const visibleIds = this._stackOrder.filter(
			(id) => this.overlays.get(id)?.isVisible === true,
		);
		const zIndexes = rankOverlayWindowZIndexes(visibleIds);
		zIndexes.forEach((zIndex, id) => {
			const overlay = this.overlays.get(id);
			if (overlay != null) {
				overlay.zIndex = zIndex;
			}
		});
	}

	private notifyListeners(): void {
		const overlays = this.getVisibleOverlays();
		this.listeners.forEach((listener) => listener(overlays));
	}

	// Utility methods for common overlay types
	public showModal(
		id: string,
		component: ComponentType<unknown>,
		props?: Record<string, unknown>,
	): void {
		this.show({
			id,
			component,
			props,
			type: 'modal',
			position: 'center',
			backdrop: true,
			backdropOpacity: 0.5,
			dismissible: true,
			animation: 'fade',
		});
	}

	public showToast(
		id: string,
		message: string,
		type: 'success' | 'error' | 'info' = 'info',
		duration = 3000,
	): void {
		this.show({
			id,
			component: () => null, // Will be handled by ToastComponent
			props: { message, type },
			type: 'toast',
			position: 'top',
			backdrop: false,
			dismissible: false,
			animation: 'slide',
		});

		// Auto-hide toast after duration
		if (duration > 0) {
			setTimeout(() => this.hide(id), duration);
		}
	}

	public showFloating(
		id: string,
		component: ComponentType<unknown>,
		props?: Record<string, unknown>,
		config?: Partial<OverlayConfig>,
	): void {
		this.show({
			id,
			component,
			props,
			type: 'floating',
			position: 'auto', // Use auto to enable responsive behavior
			backdrop: false,
			draggable: true,
			minimizable: true,
			persistent: true,
			dismissible: true,
			animation: 'scale',
			responsive: true, // Enable responsive by default for floating overlays
			showDragHandle: true,
			showClose: true,
			...config, // Allow custom config to override defaults
		});
	}

	/**
	 * Toggle collapse state for an overlay (mobile bottom sheet)
	 */
	public toggleCollapse(id: string): void {
		const overlay = this.overlays.get(id);

		if (overlay) {
			overlay.isCollapsed = !overlay.isCollapsed;
			overlay.updatedAt = new Date();
			this.notifyListeners();
		}
	}
}
